/**
 * Fullscreen Utilities
 * 
 * Handles entering and exiting fullscreen mode with cross-browser compatibility
 */

export interface FullscreenAPI {
    requestFullscreen: string;
    exitFullscreen: string;
    fullscreenElement: string;
    fullscreenEnabled: string;
    fullscreenchange: string;
    fullscreenerror: string;
}

/**
 * Get the correct fullscreen API methods for the current browser
 */
function getFullscreenAPI(): FullscreenAPI | null {
    if (typeof document === 'undefined') return null;

    // Standard API
    if ('requestFullscreen' in document.documentElement) {
        return {
            requestFullscreen: 'requestFullscreen',
            exitFullscreen: 'exitFullscreen',
            fullscreenElement: 'fullscreenElement',
            fullscreenEnabled: 'fullscreenEnabled',
            fullscreenchange: 'fullscreenchange',
            fullscreenerror: 'fullscreenerror'
        };
    }

    // Webkit (Safari)
    if ('webkitRequestFullscreen' in document.documentElement) {
        return {
            requestFullscreen: 'webkitRequestFullscreen',
            exitFullscreen: 'webkitExitFullscreen',
            fullscreenElement: 'webkitFullscreenElement',
            fullscreenEnabled: 'webkitFullscreenEnabled',
            fullscreenchange: 'webkitfullscreenchange',
            fullscreenerror: 'webkitfullscreenerror'
        };
    }

    // Mozilla (older Firefox)
    if ('mozRequestFullScreen' in document.documentElement) {
        return {
            requestFullscreen: 'mozRequestFullScreen',
            exitFullscreen: 'mozCancelFullScreen',
            fullscreenElement: 'mozFullScreenElement',
            fullscreenEnabled: 'mozFullScreenEnabled',
            fullscreenchange: 'mozfullscreenchange',
            fullscreenerror: 'mozfullscreenerror'
        };
    }

    // Microsoft (older Edge/IE)
    if ('msRequestFullscreen' in document.documentElement) {
        return {
            requestFullscreen: 'msRequestFullscreen',
            exitFullscreen: 'msExitFullscreen',
            fullscreenElement: 'msFullscreenElement',
            fullscreenEnabled: 'msFullscreenEnabled',
            fullscreenchange: 'MSFullscreenChange',
            fullscreenerror: 'MSFullscreenError'
        };
    }

    return null;
}

/**
 * Check if fullscreen is supported
 */
export function isFullscreenSupported(): boolean {
    return getFullscreenAPI() !== null;
}

/**
 * Check if currently in fullscreen mode
 */
export function isFullscreen(): boolean {
    const api = getFullscreenAPI();
    if (!api) return false;

    return !!(document as any)[api.fullscreenElement];
}

/**
 * Request fullscreen mode
 */
export async function enterFullscreen(element?: HTMLElement): Promise<boolean> {
    const api = getFullscreenAPI();
    if (!api) {
        console.warn('Fullscreen API is not supported');
        return false;
    }

    const targetElement = element || document.documentElement;

    try {
        await (targetElement as any)[api.requestFullscreen]();
        return true;
    } catch (error) {
        console.error('Failed to enter fullscreen:', error);
        return false;
    }
}

/**
 * Exit fullscreen mode
 */
export async function exitFullscreen(): Promise<boolean> {
    const api = getFullscreenAPI();
    if (!api) {
        console.warn('Fullscreen API is not supported');
        return false;
    }

    if (!isFullscreen()) {
        return true; // Already not in fullscreen
    }

    try {
        await (document as any)[api.exitFullscreen]();
        return true;
    } catch (error) {
        console.error('Failed to exit fullscreen:', error);
        return false;
    }
}

/**
 * Toggle fullscreen mode
 */
export async function toggleFullscreen(element?: HTMLElement): Promise<boolean> {
    if (isFullscreen()) {
        return await exitFullscreen();
    } else {
        return await enterFullscreen(element);
    }
}

/**
 * Add fullscreen change event listener
 */
export function onFullscreenChange(callback: (isFullscreen: boolean) => void): () => void {
    const api = getFullscreenAPI();
    if (!api) {
        return () => {}; // No-op cleanup function
    }

    const handleChange = () => {
        callback(isFullscreen());
    };

    document.addEventListener(api.fullscreenchange, handleChange);

    // Return cleanup function
    return () => {
        document.removeEventListener(api.fullscreenchange, handleChange);
    };
}

/**
 * Request fullscreen on user interaction (e.g., button click)
 * This is the recommended way to enter fullscreen due to browser security requirements
 */
export function requestFullscreenOnInteraction(element?: HTMLElement): Promise<boolean> {
    return new Promise((resolve) => {
        const handleInteraction = async () => {
            const success = await enterFullscreen(element);
            resolve(success);
            
            // Remove listeners after first interaction
            document.removeEventListener('click', handleInteraction);
            document.removeEventListener('touchstart', handleInteraction);
            document.removeEventListener('keydown', handleInteraction);
        };

        // Listen for user interactions
        document.addEventListener('click', handleInteraction, { once: true });
        document.addEventListener('touchstart', handleInteraction, { once: true });
        document.addEventListener('keydown', handleInteraction, { once: true });
    });
} 